import React, { useRef, useEffect, useState } from "react";
import "./ExperienceStyle.css";

import areenLogo    from "../images/areenalnokhbh_logo.jpeg";
import sadounLogo   from "../images/alsadounlaw_logo.jpeg";
import qimamLogo    from "../images/qimamfoundation_logo.jpeg";
import studentLogo  from "../images/studentpart_logo.jpeg";
import leaseholdLogo from "../images/leasehold_sa_logo.jpeg";
import samsungLogo  from "../images/smasung_logo.jpeg";
import appleLogo    from "../images/adatwq_logo.jpeg";

const experiences = [
  {
    id: 1,
    role: "IT Systems Engineer",
    company: "Areen Alnokhba Holding Group",
    period: "Feb 2026 – Apr 2026",
    type: "Full-Time",
    logo: areenLogo,
    accent: "#C9A84C",
    description:
      "Led digital transformation initiatives across five companies by developing AI-powered systems, corporate websites, automation workflows, and scalable IT infrastructure. Managed enterprise platforms, AI integration, and technology operations to improve efficiency, streamline business processes, and accelerate organizational growth.",
    tags: ["Digital Transformation", "AI Systems", "IT Infrastructure", "Automation"],
  },
  {
    id: 2,
    role: "AI / Software Engineer",
    company: "Bashayer Al-Saadoun Law Firm",
    period: "May 2026 – Jul 2026",
    type: "Full-Time",
    logo: sadounLogo,
    accent: "#1E3A5F",
    description:
      "Led the development of AI-powered platforms, enterprise web applications, and internal digital systems supporting the firm's digital transformation. Designed scalable full-stack solutions, integrated AI technologies, automated business workflows, and collaborated with executive stakeholders to deliver secure, production-ready software.",
    tags: ["AI Platforms", "Full-Stack", "Enterprise Systems", "LegalTech"],
  },
  {
    id: 3,
    role: "McKinsey & Co. Qimam Fellow '26",
    company: "Qimam Foundation",
    period: "Jun 2026 – Jul 2026",
    type: "Fellowship",
    logo: qimamLogo,
    accent: "#F59E0B",
    description:
      "Selected among the Top 50 fellows from 18,000+ applicants across Saudi Arabia to join one of the Kingdom's most prestigious leadership fellowships. Completed an intensive leadership program featuring executive mentoring, strategic workshops, and one-on-one sessions with ministers, CEOs, and senior executives.",
    tags: ["Leadership", "Executive Mentoring", "Strategy", "Top 50 / 18,000+"],
  },
  {
    id: 4,
    role: "Software Engineer & Project Manager",
    company: "Student Partnership Program – King Saud University",
    period: "Apr 2026 – Jul 2026",
    type: "Part-Time",
    logo: studentLogo,
    accent: "#059669",
    description:
      "Led the end-to-end development of the Student Partnership platform, officially adopted by King Saud University. Managed the Wejha UI/UX Bootcamp and hackathon, leading cross-functional teams, stakeholder collaboration, and successful project delivery.",
    tags: ["Project Management", "Full-Stack", "EdTech", "KSU Official"],
  },
  {
    id: 5,
    role: "Software Engineer Co-op",
    company: "Leasehold",
    period: "Jun 2026 – Sep 2026",
    type: "Co-op",
    logo: leaseholdLogo,
    accent: "#7C3AED",
    description:
      "Developing Mnam, a full-stack property management platform for real estate operations. Building scalable frontend and backend systems, integrating REST APIs and databases, and delivering secure digital solutions for property, tenant, contract, and maintenance management.",
    tags: ["Full-Stack", "PropTech", "REST APIs", "Real Estate Platform"],
  },
  {
    id: 6,
    role: "AI Engineer Trainee",
    company: "Samsung Innovation Campus × Misk Foundation",
    period: "Jul 2025 – Sep 2025",
    type: "Training",
    logo: samsungLogo,
    accent: "#1428A0",
    description:
      "Completed an intensive Artificial Intelligence program covering Machine Learning, Deep Learning, Computer Vision, and NLP. Led the development of Sila, an AI-powered Arabic Sign Language translation platform as Project Leader, AI Engineer, and Full-Stack Developer.",
    tags: ["AI / ML", "Computer Vision", "Deep Learning", "Project Lead"],
  },
  {
    id: 7,
    role: "AI Foundation Trainee",
    company: "Apple Developer Academy × Tuwaiq Academy",
    period: "Jul 2025 – Aug 2025",
    type: "Training",
    logo: appleLogo,
    accent: "#555555",
    description:
      "Selected among 66 participants nationwide to complete Apple's Foundation Program. Built an AI-powered iOS application using Swift, Core ML, and Design Thinking while collaborating with Apple mentors on real-world product development.",
    tags: ["Swift", "SwiftUI", "Core ML", "iOS Development"],
  },
];

function ExperienceCard({ exp, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const isActive = exp.period.includes("Present");

  return (
    <div
      ref={ref}
      className={`exp-card ${visible ? "exp-card--in" : ""}`}
      style={{ "--delay": `${index * 0.08}s`, "--accent": exp.accent }}
    >
      {/* Left accent bar */}
      <div className="exp-card__bar" style={{ background: exp.accent }} />

      {/* Logo */}
      <div className="exp-card__logo-wrap" style={{ borderColor: `${exp.accent}44` }}>
        <img src={exp.logo} alt={exp.company} className="exp-card__logo" />
      </div>

      {/* Content */}
      <div className="exp-card__content">
        <div className="exp-card__top">
          <div className="exp-card__meta">
            <span
              className={`exp-card__type ${isActive ? "exp-card__type--active" : ""}`}
              style={!isActive ? { color: exp.accent, borderColor: `${exp.accent}44` } : {}}
            >
              {isActive && <span className="exp-card__live-dot" />}
              {exp.type}
            </span>
            <span className="exp-card__period">{exp.period}</span>
          </div>
          <span className="exp-card__num">#{String(index + 1).padStart(2, "0")}</span>
        </div>

        <h3 className="exp-card__role">{exp.role}</h3>
        <div className="exp-card__company">{exp.company}</div>
        <p className="exp-card__desc">{exp.description}</p>

        <div className="exp-card__tags">
          {exp.tags.map((t) => (
            <span key={t} className="exp-card__tag" style={{ borderColor: `${exp.accent}55`, color: exp.accent }}>
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Experience({ id }) {
  return (
    <section className="exp-section" id={id}>
      <div className="exp-header">
        <span className="exp-header__eyebrow">Career</span>
        <h2 className="exp-header__title">Experience</h2>
        <p className="exp-header__sub">{experiences.length} roles across AI, engineering & leadership</p>
      </div>

      <div className="exp-list">
        {experiences.map((exp, i) => (
          <ExperienceCard key={exp.id} exp={exp} index={i} />
        ))}
      </div>
    </section>
  );
}
